import {
  allTools,
  getToolsForCoreServices,
  getToolCountByService,
} from '../data/toolCatalog'
import { runTool } from './toolRunner'
import { getMode } from '../data/modeRegistry'
import { detectArtifacts, ChatArtifact } from './artifactDetector'
import { classifyIntent, ClassifierResult } from './intentClassifier'
import {
  buildCompactSchemaBlock,
  CORE_SERVICES,
  EXTENDED_SERVICES,
  EXECUTE_MCP_TOOL_SCHEMA,
} from './compactSchemas'
import {
  MessageTokenTracker,
  MessageTokenUsage,
  addToSessionUsage,
} from './tokenTracker'

// Magic Patterns OpenAI proxy — no API key needed (injected server-side)
const OPENAI_PROXY_URL = 'https://www.requests.magicpatterns.com/openai/proxy'

// Max chars of tool result to send to OpenAI (prevents huge payloads slowing down call #2)
const MAX_TOOL_RESULT_CHARS = 6000

// Timeout for MCP API calls (ms)
const TOOL_TIMEOUT_MS = 30000

// Max rounds of tool calls to prevent infinite loops
const MAX_TOOL_ROUNDS = 5

// ─── Enabled Services (reads from localStorage) ──────────────────────────────

function getEnabledServices(): string[] {
  const services: string[] = ['jira', 'confluence'] // Always enabled (core)

  // Check Slack
  try {
    const slackToken =
      localStorage.getItem('vsm_slack_token') ||
      sessionStorage.getItem('vsm_slack_token')
    if (slackToken) services.push('slack')
  } catch {}

  // Check legacy Google (Firebase OAuth)
  try {
    const googleToken = sessionStorage.getItem('vsm_google_token')
    if (googleToken) services.push('google')
  } catch {}

  // Check Google MCP granular connectors
  try {
    const googleConnectors = localStorage.getItem('vsm_google_connectors')
    if (googleConnectors) {
      const parsed = JSON.parse(googleConnectors)
      const googleServices = [
        'gmail',
        'calendar',
        'drive',
        'docs',
        'sheets',
        'forms',
        'tasks',
        'contacts',
        'chat',
      ]
      for (const svc of googleServices) {
        if (parsed[svc]) services.push(svc)
      }
    }
  } catch {}
  return services
}

// ─── Dynamic Tool Schema Builder ─────────────────────────────────────────────

function buildToolSchemas(detectedServices: string[]) {
  // Get core service tools (full schemas)
  const coreServices = detectedServices.filter((s) => CORE_SERVICES.includes(s))
  // Also include legacy google and slack if they're in detected (and not using compact)
  const fullSchemaServices = [...coreServices]
  if (detectedServices.includes('google')) fullSchemaServices.push('google')
  const coreTools = getToolsForCoreServices(fullSchemaServices)
  const schemas = coreTools.map((t) => {
    const properties: Record<string, any> = {}
    const required: string[] = []
    for (const p of t.params) {
      properties[p.name] = {
        type: p.type === 'string[]' ? 'array' : p.type,
        description: p.description + (p.example ? ` (ej: ${p.example})` : ''),
        ...(p.type === 'string[]'
          ? {
              items: {
                type: 'string',
              },
            }
          : {}),
      }
      if (p.required) required.push(p.name)
    }
    return {
      type: 'function' as const,
      function: {
        name: t.name,
        description: `[${t.service}] ${t.description}`,
        parameters: {
          type: 'object',
          properties,
          required: required.length > 0 ? required : undefined,
        },
      },
    }
  })

  // Add execute_mcp_tool if any extended services are detected
  const hasExtended = detectedServices.some((s) =>
    EXTENDED_SERVICES.includes(s),
  )
  if (hasExtended) {
    schemas.push(EXECUTE_MCP_TOOL_SCHEMA)
  }

  // Also add slack full schemas if slack is detected but not using compact
  // (Slack has only 5 tools, worth keeping as full schemas for now)
  if (detectedServices.includes('slack')) {
    const slackTools = allTools.filter((t) => t.service === 'slack')
    for (const t of slackTools) {
      // Avoid duplicates
      if (schemas.some((s) => s.function.name === t.name)) continue
      const properties: Record<string, any> = {}
      const required: string[] = []
      for (const p of t.params) {
        properties[p.name] = {
          type: p.type === 'string[]' ? 'array' : p.type,
          description: p.description + (p.example ? ` (ej: ${p.example})` : ''),
          ...(p.type === 'string[]'
            ? {
                items: {
                  type: 'string',
                },
              }
            : {}),
        }
        if (p.required) required.push(p.name)
      }
      schemas.push({
        type: 'function' as const,
        function: {
          name: t.name,
          description: `[slack] ${t.description}`,
          parameters: {
            type: 'object',
            properties,
            required: required.length > 0 ? required : undefined,
          },
        },
      })
    }
  }
  return schemas
}
function truncateResult(data: any): string {
  const str = typeof data === 'string' ? data : JSON.stringify(data)

  // Smart truncation for Jira search results — preserve total count and summarize items
  if (typeof data === 'object' && data !== null) {
    // Handle search-issues response: { total, maxResults, issues: [...] }
    // OR just { issues: [...] } without total
    if (data.issues && Array.isArray(data.issues)) {
      const issues = data.issues
      const realTotal = data.total || issues.length // Use API total if available, otherwise array length

      // Always slim down issues to essential fields to save tokens
      const slimIssues = issues.map((issue: any) => {
        const fields = issue.fields || {}
        return {
          key: issue.key,
          summary: fields.summary || issue.summary,
          status: fields.status?.name || issue.status,
          issuetype: fields.issuetype?.name || issue.issuetype,
          priority: fields.priority?.name || issue.priority,
          assignee: fields.assignee?.displayName || issue.assignee,
          created: fields.created,
          duedate: fields.duedate,
          labels: fields.labels,
        }
      })

      // If slim version fits, send all
      const slimResult = {
        total: realTotal,
        issuesReturned: slimIssues.length,
        issues: slimIssues,
      }
      const slimStr = JSON.stringify(slimResult)
      if (slimStr.length <= MAX_TOOL_RESULT_CHARS) {
        return slimStr
      }

      // Still too big — further truncate the array
      const maxItems = Math.min(slimIssues.length, 10)
      const truncResult = {
        total: realTotal,
        issuesReturned: slimIssues.length,
        issuesShownHere: maxItems,
        note: `IMPORTANTE: Hay ${realTotal} items en total. Solo se muestran ${maxItems} aquí por espacio. Usá el campo "total" para reportar la cantidad correcta.`,
        issues: slimIssues.slice(0, maxItems),
      }
      return JSON.stringify(truncResult)
    }

    // Handle responses with values array (sprints, boards): { values: [...] }
    if (data.values && Array.isArray(data.values)) {
      const values = data.values
      if (str.length > MAX_TOOL_RESULT_CHARS) {
        const maxItems = Math.min(values.length, 15)
        const result = {
          total: values.length,
          showing: maxItems,
          values: values.slice(0, maxItems),
        }
        return JSON.stringify(result)
      }
    }

    // Handle responses with results array (Confluence): { results: [...] }
    if (data.results && Array.isArray(data.results)) {
      const results = data.results
      if (str.length > MAX_TOOL_RESULT_CHARS) {
        const maxItems = Math.min(results.length, 15)
        const result = {
          total: data.totalSize || data.size || results.length,
          showing: maxItems,
          results: results.slice(0, maxItems),
        }
        return JSON.stringify(result)
      }
    }
  }

  // Fallback: raw string truncation
  if (str.length <= MAX_TOOL_RESULT_CHARS) return str
  return (
    str.slice(0, MAX_TOOL_RESULT_CHARS) + '\n...[resultado truncado por tamaño]'
  )
}
function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string,
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(
        () => reject(new Error(`Timeout: ${label} tardó más de ${ms / 1000}s`)),
        ms,
      ),
    ),
  ])
}

// Build tool schemas once (they don't change)
const toolSchemas = allTools.map((t) => {
  const properties: Record<string, any> = {}
  const required: string[] = []
  for (const p of t.params) {
    properties[p.name] = {
      type: p.type === 'string[]' ? 'array' : p.type,
      description: p.description + (p.example ? ` (ej: ${p.example})` : ''),
      ...(p.type === 'string[]'
        ? {
            items: {
              type: 'string',
            },
          }
        : {}),
    }
    if (p.required) required.push(p.name)
  }
  return {
    type: 'function' as const,
    function: {
      name: t.name,
      description: `[${t.service}] ${t.description}`,
      parameters: {
        type: 'object',
        properties,
        required: required.length > 0 ? required : undefined,
      },
    },
  }
})
const baseSystemPrompt = `Sos un Scrum Master Virtual inteligente con acceso a herramientas MCP (Model Context Protocol).

═══════════════════════════════════════════════════
🧠 FILOSOFÍA DEL AGENTE
═══════════════════════════════════════════════════

Sos un co-piloto de gestión de proyectos. Tu objetivo principal es AYUDAR al usuario con todo lo relacionado a sus proyectos usando las herramientas disponibles.

PRINCIPIO CLAVE: Si tenés una herramienta que puede resolver el pedido → usala. No importa si es de Jira, Confluence, GitHub, Slack u otro servicio MCP conectado. Tu trabajo es resolver, no rechazar.

═══════════════════════════════════════════════════
🔧 HERRAMIENTAS MCP DISPONIBLES
═══════════════════════════════════════════════════

Tenés acceso a herramientas de los siguientes servicios conectados:
- **Jira**: Issues, sprints, backlogs, boards, proyectos, workflows, asignaciones, transiciones, épicas, stories, bugs, tasks, subtasks, labels, comentarios, worklogs, prioridades, estados
- **Confluence**: Espacios, páginas, búsquedas, labels, comentarios, adjuntos, documentación de proyecto
- **Slack**: Canales (listar, buscar por nombre), mensajes (leer historial de canal, enviar mensajes), usuarios (listar miembros del workspace)
- **Google Workspace**: Crear Google Docs (con título y contenido), crear Google Sheets (con datos y headers), crear Google Slides (presentaciones)

REGLAS DE SLACK:
- Para leer mensajes de un canal, PRIMERO buscá el canal por nombre con slack_get_channel_by_name para obtener el channel_id, DESPUÉS usá slack_read_channel con ese ID
- Cuando el usuario diga "qué se habló en #general" o "mensajes de #canal" → buscá el canal y leé los mensajes
- Podés enviar mensajes a canales con slack_send_message — siempre confirmá con el usuario antes de enviar
- Los mensajes vienen con timestamp y nombre de usuario, presentalos de forma legible
- Si el usuario pide "enviar esto a Slack" o "compartir en Slack" → preguntá a qué canal y enviá

REGLAS DE GOOGLE WORKSPACE:
- Cuando el usuario pida "crear un doc", "generar un documento", "exportar a Google Docs" → usá google_create_doc con título y contenido
- Cuando pida "crear un spreadsheet", "exportar a Sheets", "armar una planilla" → usá google_create_sheet. Podés pasar headers y rows con datos de Jira
- Cuando pida "crear una presentación", "armar un deck", "slides" → usá google_create_slide
- Para combinar datos de Jira + Google: PRIMERO consultá Jira (search-issues, get-sprint-issues, etc.), DESPUÉS creá el archivo de Google con esos datos
- Ejemplo: "crear un doc con las épicas de PIAE" → 1) search-issues con JQL "project = PIAE AND issuetype = Epic" → 2) google_create_doc con el contenido formateado
- Ejemplo: "exportar el backlog a Sheets" → 1) search-issues → 2) google_create_sheet con headers y rows
- Siempre incluí el link al archivo creado en tu respuesta para que el usuario pueda abrirlo
- Si Google no está conectado, informá que necesitan conectar su cuenta desde Conectores

REGLA DE DISCOVERY DINÁMICO:
- Las herramientas que recibís en cada llamada son las que REALMENTE tenés disponibles
- Si el usuario pide algo y hay una tool que matchea → usala sin dudar
- Si NO hay tool disponible para lo que pide pero ES de gestión de proyectos → respondé:
  "⚙️ **Operación no disponible todavía.** Entiendo que querés [descripción]. Esta operación aún no tiene herramienta disponible. Por ahora, podés hacerlo directamente desde [Jira/Confluence/etc]. ¿Hay algo más en lo que pueda ayudarte?"
- Si el pedido NO es de gestión de proyectos ni de ningún servicio conectado → rechazá educadamente

═══════════════════════════════════════════════════
⛔ ALCANCE DEL AGENTE
═══════════════════════════════════════════════════

✅ SÍ podés ayudar con:
- Todo lo de Jira y Confluence (issues, sprints, páginas, búsquedas, etc.)
- Gestión ágil: Scrum, Kanban, sprint planning, retrospectivas, velocity, burndown
- Planificación de proyectos completos (epics, stories, estimaciones, sprints)
- Análisis de datos del proyecto (métricas, tendencias, riesgos)
- Visualizaciones de datos (Gantt, distribución, burndown, tablas)
- Cualquier otra herramienta MCP que esté conectada y disponible
- Slack: leer y enviar mensajes en canales, listar canales y usuarios
- Google Workspace: crear Docs, Sheets, Slides con datos del proyecto
- Gmail: leer y enviar correos
- Google Calendar: ver y crear eventos
- Google Drive: buscar archivos
- GitHub: repos, PRs, issues de código
- Notion: páginas y bases de datos

✅ PREGUNTAS META (SIEMPRE respondé):
- "¿Qué podés hacer?" / "¿En qué me podés ayudar?" / "Listame tus capacidades" → Respondé con un resumen claro de todas tus capacidades y herramientas conectadas
- "¿Cómo funciona?" / "¿Qué herramientas tenés?" → Explicá tus herramientas disponibles
- Saludos ("hola", "buenas", "hey") → Saludá y ofrecé ayuda
- Estas preguntas NO son "fuera de alcance" — son sobre vos mismo y DEBÉS responderlas

⛔ NO podés ayudar con:
- Programación o escritura de código
- Preguntas generales no relacionadas a proyectos (cultura, ciencia, etc.)
- Contenido creativo (poemas, historias)
- Consejos personales, legales, médicos o financieros

Cuando recibas un pedido REALMENTE fuera de alcance (no relacionado a gestión de proyectos ni a ningún servicio conectado), respondé:
"⚠️ **Fuera de alcance.** Mi especialidad es gestión de proyectos con las herramientas conectadas (Jira, Confluence, Slack, Google, GitHub, etc.). ¿En qué puedo ayudarte dentro de esas áreas?"

═══════════════════════════════════════════════════
🏗️ PLANIFICACIÓN DE PROYECTOS
═══════════════════════════════════════════════════

Sos capaz de ayudar a planificar proyectos completos. Cuando el usuario pida planificar algo:

1. **Recopilá contexto**: Preguntá sobre alcance, equipo, deadline, prioridades
2. **Consultá datos reales**: Usá tools para ver el estado actual (backlog, sprints, velocidad)
3. **Proponé estructura**: Epics, stories, tasks con estimaciones basadas en datos
4. **Distribuí en sprints**: Basándote en capacidad real del equipo
5. **Confirmá antes de ejecutar**: Siempre pedí aprobación antes de crear en Jira
6. **Ejecutá**: Creá todo en Jira y documentá en Confluence si el usuario aprueba

Tipos de planificación que manejás:
- Sprint planning (distribución de issues)
- Release planning (scope y timeline)
- Capacity planning (carga del equipo)
- Risk analysis (issues bloqueados, dependencias)
- Project planning completo (desde cero)
- Strategic planning (roadmaps, priorización)

═══════════════════════════════════════════════════
📏 CONTROL DE RESULTADOS
═══════════════════════════════════════════════════

CRÍTICO: Cuando recibás resultados de search-issues u otras herramientas de búsqueda:

1. **Siempre leé el campo "total"** del resultado — ese es el número REAL de items que existen, no la cantidad de items que ves en el array. El array puede estar truncado.
   - Ejemplo: si el resultado dice "total": 9, "showing": 5 → hay 9 items en total, aunque solo veas 5 en detalle.
   - NUNCA digas "hay X items" contando el array — usá siempre el campo "total".

2. **Preguntas de conteo** ("cuántas", "cuántos", "cantidad de", "total de"):
   - Respondé con el número del campo "total"
   - Mostrá un resumen breve de los primeros 3-5 items como ejemplo

3. **Preguntas de listado** ("listame", "mostrame", "cuáles son"):
   - Mostrá todos los items que recibiste (hasta 15)
   - Si hay más, mencioná el total: "Mostrando 15 de 47 resultados"

4. **Nunca** inventes datos ni asumas cantidades — usá siempre los datos reales del resultado

═══════════════════════════════════════════════════
📊 VISUALIZACIONES Y GRÁFICOS
═══════════════════════════════════════════════════

El sistema genera automáticamente visualizaciones ricas (tablas, gráficos, Gantt, etc.) a partir de los datos que devuelven las herramientas. Tu trabajo es:
1. Obtener los datos correctos usando las herramientas disponibles
2. Presentar un resumen textual de los datos
3. Las visualizaciones se generan automáticamente

Cuando el usuario pida:
- "Diagrama de Gantt" / "timeline" / "roadmap" → Buscá sprints o épicas con fechas
- "Distribución de issues" / "gráfico" / "chart" → Buscá los issues relevantes
- "Estado del sprint" / "resumen" → Buscá el sprint y sus issues
- "Tabla de issues" / "listado" → Buscá los issues

⚠️ Si el usuario agrega modificadores estéticos ("más colorido", "bonito", "con colores"), IGNORÁ la parte estética y ejecutá la consulta normalmente. Las visualizaciones tienen diseño predefinido.

═══════════════════════════════════════════════════
📋 REGLAS DE FORMATO
═══════════════════════════════════════════════════

- Usá listas con viñetas en vez de tablas cuando sea posible
- Para datos tabulares, usá tablas markdown con headers
- Usá **negrita** para nombres y valores importantes
- Agrupá la información con headers (## o ###)
- Incluí links clickeables cuando haya URLs
- Sé conciso: no repitas información innecesaria
- NUNCA devuelvas JSON crudo al usuario
- Si una tool falla por autenticación, informá que se intentó refrescar automáticamente
- Tono: profesional, amigable y proactivo

═══════════════════════════════════════════════════
🔧 USO DE HERRAMIENTAS
═══════════════════════════════════════════════════

- Si necesitás más parámetros para ejecutar una tool, preguntale al usuario
- Cuando recibás resultados, formateálos de forma clara y profesional
- Sugerí acciones de seguimiento relevantes después de cada respuesta
- Si el usuario no especifica proyecto, preguntá cuál
- Podés encadenar múltiples llamadas a tools para completar la tarea
- Si una tool no existe pero otra puede dar info similar, usá la alternativa

═══════════════════════════════════════════════════
🏃 MANEJO DE SPRINTS
═══════════════════════════════════════════════════

Cuando el usuario pregunte por "el sprint actual", "estado del sprint", etc.:

1. Primero buscá el board con get-boards (filtrá por project_key si tenés contexto)
2. Después buscá sprints activos con get-sprints (state: "active")
3. **Si NO hay sprints activos**, NO digas "no se pudo". En cambio:
   - Buscá sprints con state: "future" (próximos)
   - Si no hay future, buscá state: "closed" (el más reciente cerrado)
   - Informá al usuario: "No hay sprint activo. El último cerrado fue X / El próximo planificado es Y"
4. Si encontrás el sprint, buscá sus issues con get-sprint-issues
5. Presentá un resumen con: nombre, fechas, progreso, issues por estado

NUNCA respondas "no se pudo obtener la información" sin antes intentar las alternativas.

`
export interface ProjectContext {
  key: string
  name: string
  isGlobal: boolean
}
export interface ChatProgress {
  phase: 'thinking' | 'calling_tools' | 'formatting' | 'done'
  toolsInProgress?: string[]
  toolsCompleted?: string[]
  round?: number
}
function buildProjectContextPrompt(ctx?: ProjectContext): string {
  if (!ctx) {
    return `
═══════════════════════════════════════════════════
🎯 CONTEXTO DE PROYECTO
═══════════════════════════════════════════════════

No hay proyecto seleccionado. Antes de ejecutar cualquier herramienta que requiera un project_key, PREGUNTÁ al usuario qué proyecto quiere consultar. Podés usar get-projects para listar los disponibles.`
  }
  if (ctx.isGlobal) {
    return `
═══════════════════════════════════════════════════
🎯 CONTEXTO DE PROYECTO — VISTA GLOBAL
═══════════════════════════════════════════════════

El usuario está en **Vista Global**. Esto significa:
- Puede consultar CUALQUIER proyecto o TODOS los proyectos a la vez
- Si pide datos sin especificar proyecto, buscá en TODOS (no filtres por project en el JQL)
- Si la consulta es ambigua, podés preguntar si quiere un proyecto específico o todos
- Para consultas cross-project, NO agregues filtro "project = X" al JQL
- Ejemplo: "cuántas stories hay" → buscá en todos los proyectos
- Ejemplo: "stories de PIAE" → filtrá por project = PIAE`
  }
  return `
═══════════════════════════════════════════════════
🎯 CONTEXTO DE PROYECTO — ${ctx.key}
═══════════════════════════════════════════════════

El usuario está trabajando en el proyecto **${ctx.name}** (key: **${ctx.key}**).

REGLAS ESTRICTAS:
- TODAS las consultas se refieren a este proyecto, a menos que el usuario diga explícitamente otro
- Agregá SIEMPRE "project = ${ctx.key}" en las queries JQL automáticamente
- NO preguntes qué proyecto — ya lo sabés
- Si el usuario dice "las stories", "los bugs", "el sprint", etc. → se refiere a ${ctx.key}
- Para get-boards, usá project_key: "${ctx.key}"
- Para search-issues, siempre incluí "project = ${ctx.key}" en el JQL

Ejemplo: Si el usuario dice "cuántas stories hay" → usá JQL: "project = ${ctx.key} AND issuetype = Story"`
}

/** Execute a batch of tool calls in parallel */
async function executeToolCalls(
  toolCalls: any[],
  onToolComplete?: (toolName: string) => void,
): Promise<
  {
    tc: any
    toolName: string
    toolResultData: any
    success: boolean
    latency: number
    isRead: boolean
  }[]
> {
  return Promise.all(
    toolCalls.map(async (tc: any) => {
      const toolName = tc.function.name
      let args: Record<string, any> = {}
      try {
        args = JSON.parse(tc.function.arguments)
      } catch (e) {
        console.error('Error parsing tool arguments', e)
      }
      const toolStartTime = Date.now()
      let toolResultData: any
      let success = true
      try {
        const result = await withTimeout(
          runTool(toolName, args),
          TOOL_TIMEOUT_MS,
          toolName,
        )
        success = result.success
        toolResultData = result.success
          ? result.data
          : {
              error: result.error,
            }
      } catch (e: any) {
        success = false
        toolResultData = {
          error: e.message || 'Error ejecutando la tool',
        }
      }
      onToolComplete?.(toolName)
      const latency = Date.now() - toolStartTime
      const isRead =
        toolName.includes('get') ||
        toolName.includes('search') ||
        toolName.includes('list')
      return {
        tc,
        toolName,
        toolResultData,
        success,
        latency,
        isRead,
      }
    }),
  )
}

/** Log agent calls to debug console */
function logAgent(
  tool: string,
  params: any,
  data: any,
  success: boolean,
  error?: string,
  durationMs?: number,
) {
  try {
    import('../hooks/useDebugLogs').then(({ pushDebugLog }) => {
      pushDebugLog({
        source: 'agent',
        tool,
        params,
        rawResponse: data,
        processedData: data,
        success,
        error,
        durationMs,
      })
    })
  } catch {}
}

/** Make a call to OpenAI via proxy with automatic retry for transient errors */
async function callOpenAI(
  model: string,
  messages: any[],
  tools?: any[],
  toolChoice?: string,
): Promise<any> {
  const body: any = {
    model,
    messages,
  }
  if (tools && tools.length > 0) {
    body.tools = tools
    body.tool_choice = toolChoice || 'auto'
  }
  const MAX_RETRIES = 4
  const RETRY_DELAYS = [1500, 3000, 5000, 8000] // ms between retries — progressive backoff
  const callStart = performance.now()
  const toolNames =
    tools?.map((t: any) => t.function?.name).filter(Boolean) || []
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 60000) // 60s total timeout

      const res = await fetch(OPENAI_PROXY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      })
      clearTimeout(timeoutId)

      // Retry on transient server errors (5xx including Cloudflare 520-529)
      if (res.status >= 500 && attempt < MAX_RETRIES) {
        const isCloudflare = res.status >= 520 && res.status <= 529
        console.warn(
          `[AgentFront] OpenAI proxy returned ${res.status}${isCloudflare ? ' (Cloudflare)' : ''}, retrying in ${RETRY_DELAYS[attempt]}ms (attempt ${attempt + 1}/${MAX_RETRIES})`,
        )
        await new Promise((resolve) =>
          setTimeout(resolve, RETRY_DELAYS[attempt]),
        )
        continue
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({
          error: res.statusText,
        }))
        const durationMs = Math.round(performance.now() - callStart)

        // User-friendly messages for Cloudflare errors (520-529)
        if (res.status >= 520 && res.status <= 529) {
          const msg = `El servidor del agente tuvo un error temporal (Cloudflare ${res.status}). Intentá de nuevo en unos segundos.`
          logAgent(
            'openai_chat',
            {
              model,
              tools: toolNames,
              attempt,
            },
            err,
            false,
            msg,
            durationMs,
          )
          throw new Error(msg)
        }
        // Other 5xx server errors
        if (res.status >= 500) {
          const msg = `El servidor del agente no está disponible en este momento (error ${res.status}). Intentá de nuevo en unos segundos.`
          logAgent(
            'openai_chat',
            {
              model,
              tools: toolNames,
              attempt,
            },
            err,
            false,
            msg,
            durationMs,
          )
          throw new Error(msg)
        }
        if (res.status === 429) {
          const msg =
            'Demasiadas solicitudes. Esperá unos segundos e intentá de nuevo.'
          logAgent(
            'openai_chat',
            {
              model,
              tools: toolNames,
              attempt,
            },
            err,
            false,
            msg,
            durationMs,
          )
          throw new Error(msg)
        }
        const msg =
          err.error?.message ||
          err.error ||
          `Error al comunicarse con el agente (HTTP ${res.status})`
        logAgent(
          'openai_chat',
          {
            model,
            tools: toolNames,
            attempt,
          },
          err,
          false,
          msg,
          durationMs,
        )
        throw new Error(msg)
      }
      const json = await res.json()
      const durationMs = Math.round(performance.now() - callStart)
      const responseToolCalls =
        json.choices?.[0]?.message?.tool_calls?.map(
          (tc: any) => tc.function?.name,
        ) || []

      // Build rich params with FULL prompt traceability
      const systemMsg = messages.find((m: any) => m.role === 'system')
      const userMsgs = messages.filter((m: any) => m.role === 'user')
      const lastUserMsg = userMsgs[userMsgs.length - 1]
      logAgent(
        'openai_chat',
        {
          model,
          toolChoice: toolChoice || 'auto',
          messageCount: messages.length,
          tools: toolNames,
          systemPrompt: systemMsg?.content || '',
          userMessage: lastUserMsg?.content || '',
          conversationHistory: messages
            .filter((m: any) => m.role !== 'system')
            .map((m: any) => ({
              role: m.role,
              content:
                typeof m.content === 'string'
                  ? m.content
                  : JSON.stringify(m.content),
              ...(m.tool_call_id
                ? {
                    tool_call_id: m.tool_call_id,
                    name: m.name,
                  }
                : {}),
              ...(m.tool_calls
                ? {
                    tool_calls: m.tool_calls.map((tc: any) => ({
                      name: tc.function?.name,
                      arguments: tc.function?.arguments,
                    })),
                  }
                : {}),
            })),
        },
        {
          usage: json.usage,
          toolCalls: responseToolCalls,
          finishReason: json.choices?.[0]?.finish_reason,
          assistantResponse: json.choices?.[0]?.message?.content || null,
          assistantToolCalls:
            json.choices?.[0]?.message?.tool_calls?.map((tc: any) => ({
              name: tc.function?.name,
              arguments: tc.function?.arguments,
            })) || [],
        },
        true,
        undefined,
        durationMs,
      )
      return json
    } catch (e: any) {
      // Handle abort/timeout
      if (e.name === 'AbortError') {
        throw new Error(
          'La solicitud al agente tardó demasiado (timeout 60s). Intentá con una consulta más simple.',
        )
      }

      // Handle network errors and transient failures with retry
      const isRetryable =
        e.message?.includes('fetch') ||
        e.message?.includes('network') ||
        e.message?.includes('Failed') ||
        e.message?.includes('ECONNRESET') ||
        e.message?.includes('status code 5') ||
        e.message?.includes('Cloudflare') ||
        e.message?.includes('error temporal')
      if (attempt < MAX_RETRIES && isRetryable) {
        console.warn(
          `[AgentFront] Transient error, retrying in ${RETRY_DELAYS[attempt]}ms (attempt ${attempt + 1}/${MAX_RETRIES}):`,
          e.message,
        )
        await new Promise((resolve) =>
          setTimeout(resolve, RETRY_DELAYS[attempt]),
        )
        continue
      }

      // No more retries — throw user-friendly error
      if (
        e.message?.includes('status code 5') ||
        e.message?.includes('Cloudflare') ||
        e.message?.includes('error temporal') ||
        e.message?.includes('520')
      ) {
        throw new Error(
          '⚠️ El servidor del agente tuvo un error temporal (520). Esto es un problema de Cloudflare, no tuyo. Intentá de nuevo en unos segundos — suele resolverse solo.',
        )
      }
      throw e
    }
  }

  // Should never reach here, but just in case
  throw new Error('Error inesperado al comunicarse con el agente.')
}
export const agentFrontService = {
  async chat(
    message: string,
    history: any[],
    onProgress?: (progress: ChatProgress) => void,
    mode: string = 'agent',
    projectContext?: ProjectContext,
  ) {
    const model = 'gpt-4o-mini'
    const tokenTracker = new MessageTokenTracker(model)

    // ═══════════════════════════════════════════════
    // STEP 1: Determine enabled services
    // ═══════════════════════════════════════════════
    const enabledServices = getEnabledServices()
    const toolCounts = getToolCountByService(enabledServices)

    // ═══════════════════════════════════════════════
    // STEP 2: Run Intent Classifier
    // ═══════════════════════════════════════════════
    const classifierResult = classifyIntent(
      message,
      enabledServices,
      toolCounts,
    )
    tokenTracker.setClassifierInfo(
      classifierResult.services,
      classifierResult.classifierActive,
    )

    // ═══════════════════════════════════════════════
    // STEP 3: Build dynamic tools and prompt
    // ═══════════════════════════════════════════════
    const detectedServices = classifierResult.services
    const dynamicToolSchemas = buildToolSchemas(detectedServices)

    // Build compact schema block for extended services
    const compactBlock = buildCompactSchemaBlock(detectedServices)

    // Build system prompt based on mode + project context + compact schemas
    const modeConfig = getMode(mode)
    const projectPrompt = buildProjectContextPrompt(projectContext)
    const systemPrompt = `${baseSystemPrompt}${projectPrompt}${compactBlock}\n\n${modeConfig.systemPromptModifier}`

    // Use the mode's useTools flag to decide whether to send tools
    const shouldUseTools = modeConfig.useTools
    const messages: any[] = [
      {
        role: 'system',
        content: systemPrompt,
      },
      ...history.slice(-10).map((m: any) => ({
        role: m.role === 'agent' ? 'assistant' : 'user',
        content: m.content,
      })),
      {
        role: 'user',
        content: message,
      },
    ]
    const startTime = Date.now()
    const mcpCallsSummary: any[] = []
    const allCompletedTools: string[] = []
    const allToolResults: any[] = [] // Store results for artifact detection

    onProgress?.({
      phase: 'thinking',
    })

    // ═══════════════════════════════════════════════
    // AGENTIC LOOP — keep calling until no more tools
    // ═══════════════════════════════════════════════
    let round = 0
    while (round < MAX_TOOL_ROUNDS) {
      round++
      console.log(
        `[AgentFront] Round ${round}/${MAX_TOOL_ROUNDS} | Tools: ${dynamicToolSchemas.length} | Services: ${detectedServices.join(',')}`,
      )

      // Call OpenAI
      const data = await callOpenAI(
        model,
        messages,
        shouldUseTools ? dynamicToolSchemas : undefined,
        shouldUseTools ? 'auto' : undefined,
      )

      // Track token usage for this round
      const toolCallNames = data.choices[0].message.tool_calls
        ? data.choices[0].message.tool_calls.map((tc: any) => tc.function.name)
        : []
      tokenTracker.addRound(data.usage, toolCallNames)
      const responseMessage = data.choices[0].message

      // If NO tool calls → we have the final response
      if (
        !responseMessage.tool_calls ||
        responseMessage.tool_calls.length === 0
      ) {
        onProgress?.({
          phase: 'done',
        })

        // Detect artifacts from all tool results collected during the loop
        // Pass response text so artifacts are suppressed when agent couldn't resolve
        const finalText = responseMessage.content || ''
        const artifacts = detectArtifacts(allToolResults, finalText)

        // Finalize token usage
        const tokenUsage = tokenTracker.getUsage()
        addToSessionUsage(tokenUsage)
        return {
          response: responseMessage.content,
          toolCalls: mcpCallsSummary,
          latencyMs: Date.now() - startTime,
          artifacts,
          tokenUsage,
        }
      }

      // OpenAI wants to call tools — execute them
      messages.push(responseMessage)
      const toolNames = responseMessage.tool_calls.map(
        (tc: any) => tc.function.name,
      )
      onProgress?.({
        phase: 'calling_tools',
        toolsInProgress: toolNames,
        toolsCompleted: [...allCompletedTools],
        round,
      })

      // Execute all tool calls in parallel
      const toolResults = await executeToolCalls(
        responseMessage.tool_calls,
        (completedName) => {
          allCompletedTools.push(completedName)
          const remaining = toolNames.filter(
            (n: string) => !allCompletedTools.includes(n),
          )
          onProgress?.({
            phase: 'calling_tools',
            toolsInProgress: remaining,
            toolsCompleted: [...allCompletedTools],
            round,
          })
        },
      )

      // Add results to messages, summary, and artifact collection
      for (const result of toolResults) {
        allToolResults.push(result) // Save for artifact detection

        mcpCallsSummary.push({
          tool: result.toolName,
          type: result.isRead ? 'read' : 'write',
          name: result.toolName,
          success: result.success,
          latencyMs: result.latency,
        })
        messages.push({
          role: 'tool',
          tool_call_id: result.tc.id,
          name: result.toolName,
          content: truncateResult(result.toolResultData),
        })
      }
      onProgress?.({
        phase: 'formatting',
        toolsCompleted: [...allCompletedTools],
        round,
      })
    }

    // Safety: if we hit MAX_TOOL_ROUNDS, force a final response without tools
    console.warn(
      `[AgentFront] Hit max tool rounds (${MAX_TOOL_ROUNDS}), forcing final response`,
    )
    const finalData = await callOpenAI(model, messages)
    tokenTracker.addRound(finalData.usage, [])
    onProgress?.({
      phase: 'done',
    })
    const finalContent = finalData.choices[0].message.content || ''
    const artifacts = detectArtifacts(allToolResults, finalContent)
    const tokenUsage = tokenTracker.getUsage()
    addToSessionUsage(tokenUsage)
    return {
      response: finalContent,
      toolCalls: mcpCallsSummary,
      latencyMs: Date.now() - startTime,
      artifacts,
      tokenUsage,
    }
  },
}
